import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import {
    Drawer, Descriptions, Input, Select, Spin, Tag, Button, Modal, Table, Typography, Space, Alert,
} from 'antd';
import { EnvironmentOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';
import classes from './canalsMap.module.css';
import i18n from '../../../../lang';
import dashBRu from '../../../../lang/locales/dashB.ru.json';
import dashBKk from '../../../../lang/locales/dashB.kk.json';
import dashBEn from '../../../../lang/locales/dashB.en.json';

// Регистрируем фрагмент-локали дашборда (часть B) без правки lang/index.js
i18n.addResourceBundle('ru', 'translation', dashBRu, true, false);
i18n.addResourceBundle('kk', 'translation', dashBKk, true, false);
i18n.addResourceBundle('en', 'translation', dashBEn, true, false);

const { Text } = Typography;

// Категории каналов → цвет маркера / радиус.
// Ключи совпадают с данными canals.json (canal.section) — НЕ переводить.
const SECTIONS = {
    'Магистральные каналы':       { color: '#1677ff', radius: 7, labelKey: 'dashboard.canals.sectionMain' },
    'Межхозяйственные каналы':    { color: '#52c41a', radius: 5, labelKey: 'dashboard.canals.sectionInter' },
    'Внутрихозяйственные каналы': { color: '#fa8c16', radius: 4, labelKey: 'dashboard.canals.sectionInternal' },
};
const DEFAULT_SECTION = { color: '#8c8c8c', radius: 5 };

const sectionStyle = (s) => SECTIONS[s] || DEFAULT_SECTION;

// Цвет тега технического состояния
const conditionColor = (c) => {
    if (!c) return 'default';
    const v = c.toLowerCase();
    if (v.includes('неудов') || v.includes('авар')) return 'red';
    if (v.includes('удов')) return 'green';
    return 'blue';
};

// Форматирование значения с единицей измерения
const fmt = (v, unit = '') => {
    if (v === null || v === undefined || v === '') return '—';
    return unit ? `${v} ${unit}` : String(v);
};

// Подгоняет карту под все точки один раз при загрузке
function FitBounds({ points }) {
    const map = useMap();
    useEffect(() => {
        if (points.length) {
            map.fitBounds(points.map((p) => [p.lat, p.lon]), { padding: [40, 40] });
        }
    }, [map]); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
}

export default function CanalsMap() {
    const { t } = useTranslation();
    const [canals, setCanals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [sectionFilter, setSectionFilter] = useState([]);

    const [selected, setSelected] = useState(null);   // выбранный канал (Drawer)
    const [noCoordOpen, setNoCoordOpen] = useState(false); // модалка «без координат»

    useEffect(() => {
        fetch('/data/canals.json')
            .then((r) => {
                if (!r.ok) throw new Error(t('dashboard.canals.loadError'));
                return r.json();
            })
            .then((data) => setCanals(data))
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [t]);

    const mapped = useMemo(() => canals.filter((c) => c.lat != null && c.lon != null), [canals]);
    const noCoord = useMemo(() => canals.filter((c) => c.lat == null || c.lon == null), [canals]);

    const sectionLabel = (s) => (SECTIONS[s]?.labelKey ? t(SECTIONS[s].labelKey) : s);

    const sectionOptions = useMemo(
        () => Array.from(new Set(canals.map((c) => c.section).filter(Boolean)))
            .map((s) => ({ label: sectionLabel(s), value: s })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [canals, t],
    );

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return mapped.filter((c) => {
            if (sectionFilter.length && !sectionFilter.includes(c.section)) return false;
            if (q && !(`${c.name} ${c.waterSource || ''} ${c.ruralOkrug || ''}`.toLowerCase().includes(q))) return false;
            return true;
        });
    }, [mapped, search, sectionFilter]);

    if (loading) {
        return (
            <div className={classes.loadingBox}>
                <Spin size="large" />
                <Text>{t('dashboard.canals.loading')}</Text>
            </div>
        );
    }

    if (error) {
        return <Alert type="error" showIcon message={t('error')} description={error} />;
    }

    return (
        <div className={classes.wrapper}>
            {/* ── Панель управления ── */}
            <div className={classes.toolbar}>
                <Input
                    className={classes.search}
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder={t('dashboard.canals.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <Select
                    className={classes.sectionSelect}
                    mode="multiple"
                    allowClear
                    placeholder={t('dashboard.canals.sectionPlaceholder')}
                    options={sectionOptions}
                    value={sectionFilter}
                    onChange={setSectionFilter}
                    maxTagCount="responsive"
                />
                <div className={classes.stats}>
                    <Text type="secondary">
                        {t('dashboard.canals.onMap', { visible: visible.length, total: mapped.length })}
                    </Text>
                    {noCoord.length > 0 && (
                        <Button size="small" onClick={() => setNoCoordOpen(true)}>
                            {t('dashboard.canals.noCoordButton', { count: noCoord.length })}
                        </Button>
                    )}
                </div>
            </div>

            {/* ── Карта ── */}
            <div className={classes.mapWrapper}>
                <MapContainer center={[43.2, 72.5]} zoom={8} className={classes.map} scrollWheelZoom>
                    <TileLayer
                        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
                    />
                    <FitBounds points={mapped} />
                    {visible.map((c) => {
                        const st = sectionStyle(c.section);
                        const isSel = selected && selected.id === c.id && selected.section === c.section;
                        return (
                            <CircleMarker
                                key={`${c.section}-${c.id}-${c.name}`}
                                center={[c.lat, c.lon]}
                                radius={isSel ? st.radius + 4 : st.radius}
                                pathOptions={{
                                    color: '#ffffff',
                                    weight: isSel ? 3 : 1.5,
                                    fillColor: st.color,
                                    fillOpacity: 0.9,
                                }}
                                eventHandlers={{ click: () => setSelected(c) }}
                            >
                                <Tooltip direction="top" offset={[0, -4]}>{c.name}</Tooltip>
                            </CircleMarker>
                        );
                    })}
                </MapContainer>

                {/* Легенда */}
                <div className={classes.legend}>
                    <span className={classes.legendTitle}>{t('dashboard.canals.legendTitle')}</span>
                    {Object.entries(SECTIONS).map(([name, st]) => (
                        <div key={name} className={classes.legendItem}>
                            <span className={classes.legendDot} style={{ background: st.color }} />
                            <span>{t(st.labelKey)}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Детали канала ── */}
            <CanalDrawer canal={selected} onClose={() => setSelected(null)} sectionLabel={sectionLabel} />

            {/* ── Каналы без координат ── */}
            <Modal
                title={t('dashboard.canals.noCoordModalTitle', { count: noCoord.length })}
                open={noCoordOpen}
                onCancel={() => setNoCoordOpen(false)}
                footer={null}
                width={760}
            >
                <Table
                    size="small"
                    rowKey={(r) => `${r.section}-${r.id}-${r.name}`}
                    dataSource={noCoord}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    columns={[
                        { title: t('dashboard.canals.colNo'), dataIndex: 'id', width: 60 },
                        { title: t('dashboard.canals.colName'), dataIndex: 'name' },
                        { title: t('dashboard.canals.colSection'), dataIndex: 'section', render: (v) => sectionLabel(v) },
                        { title: t('dashboard.canals.colOkrug'), dataIndex: 'ruralOkrug', render: (v) => v || '—' },
                        {
                            title: t('dashboard.canals.colReason'),
                            dataIndex: 'coordNote',
                            render: (v) => v || t('dashboard.canals.noCoordReason'),
                        },
                        {
                            title: '',
                            width: 90,
                            render: (_, r) => (
                                <Button type="link" size="small" onClick={() => { setNoCoordOpen(false); setSelected(r); }}>
                                    {t('dashboard.canals.details')}
                                </Button>
                            ),
                        },
                    ]}
                />
            </Modal>
        </div>
    );
}

// ── Drawer со всей информацией о канале ──
function CanalDrawer({ canal, onClose, sectionLabel }) {
    const { t } = useTranslation();
    if (!canal) return null;

    const Group = ({ title, items }) => {
        const rows = items.filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== '—');
        if (!rows.length) return null;
        return (
            <>
                <div className={classes.groupTitle}>{title}</div>
                <Descriptions bordered size="small" column={1}>
                    {rows.map(([label, value]) => (
                        <Descriptions.Item key={label} label={label}>{value}</Descriptions.Item>
                    ))}
                </Descriptions>
            </>
        );
    };

    return (
        <Drawer
            open={!!canal}
            onClose={onClose}
            width={520}
            title={
                <Space direction="vertical" size={2}>
                    <span className={classes.drawerName}>
                        <EnvironmentOutlined style={{ color: sectionStyle(canal.section).color, marginRight: 8 }} />
                        {canal.name}
                    </span>
                    <Space size={6} wrap>
                        <Tag color="processing">№ {canal.id}</Tag>
                        {canal.section && <Tag>{sectionLabel ? sectionLabel(canal.section) : canal.section}</Tag>}
                        {canal.condition && <Tag color={conditionColor(canal.condition)}>{canal.condition}</Tag>}
                    </Space>
                </Space>
            }
        >
            {canal.coordNote && (
                <Alert
                    type={canal.lat == null ? 'warning' : 'info'}
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={canal.coordNote}
                />
            )}

            <Group
                title={t('dashboard.canals.groupGeneral')}
                items={[
                    [t('dashboard.canals.waterSource'), fmt(canal.waterSource)],
                    [t('dashboard.canals.commissionYear'), fmt(canal.commissionYear)],
                    [t('dashboard.canals.balanceYear'), fmt(canal.balanceYear)],
                    [t('dashboard.canals.capacity'), fmt(canal.capacity, 'м³/с')],
                ]}
            />

            <Group
                title={t('dashboard.canals.groupLengthBefore')}
                items={[
                    [t('dashboard.canals.total'), fmt(canal.beforeReconTotalKm, 'км')],
                    [t('dashboard.canals.earth'), fmt(canal.beforeReconEarthKm, 'км')],
                    [t('dashboard.canals.lined'), fmt(canal.beforeReconLinedKm, 'км')],
                    [t('dashboard.canals.acceptanceAct'), fmt(canal.acceptanceAct)],
                ]}
            />

            <Group
                title={t('dashboard.canals.groupLengthAfter')}
                items={[
                    [t('dashboard.canals.total'), fmt(canal.afterReconTotalKm, 'км')],
                    [t('dashboard.canals.earth'), fmt(canal.afterReconEarthKm, 'км')],
                    [t('dashboard.canals.lined'), fmt(canal.afterReconLinedKm, 'км')],
                ]}
            />

            <Group
                title={t('dashboard.canals.groupArea')}
                items={[
                    [t('dashboard.canals.areaRegular'), fmt(canal.areaRegularHa, 'га')],
                    [t('dashboard.canals.areaLiman'), fmt(canal.areaLimanHa, 'га')],
                    [t('dashboard.canals.areaFlooded'), fmt(canal.areaFloodedHa, 'га')],
                ]}
            />

            <Group
                title={t('dashboard.canals.groupEfficiency')}
                items={[
                    [t('dashboard.canals.efficiencyDesign'), fmt(canal.efficiencyDesign)],
                    [t('dashboard.canals.efficiencyActual'), fmt(canal.efficiencyActual)],
                ]}
            />

            <Group
                title={t('dashboard.canals.groupCondition')}
                items={[
                    [t('dashboard.canals.wear'), fmt(canal.wearPercent)],
                    [t('dashboard.canals.technicalCondition'), fmt(canal.condition)],
                    [t('dashboard.canals.params'), fmt(canal.params)],
                ]}
            />

            <Group
                title={t('dashboard.canals.groupAdmin')}
                items={[
                    [t('dashboard.canals.district'), fmt(canal.servedDistrict)],
                    [t('dashboard.canals.ruralOkrug'), fmt(canal.ruralOkrug)],
                    [t('dashboard.canals.structuresCount'), fmt(canal.structuresCount)],
                    [t('dashboard.canals.cadastralNumber'), fmt(canal.cadastralNumber)],
                    [t('dashboard.canals.stateAct'), fmt(canal.stateAct)],
                ]}
            />

            <Group
                title={t('dashboard.canals.groupCoords')}
                items={[
                    [t('dashboard.canals.latitude'), fmt(canal.lat)],
                    [t('dashboard.canals.longitude'), fmt(canal.lon)],
                ]}
            />
        </Drawer>
    );
}
